import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Trash2, Eye, Download, Search, RefreshCw, X, Mail, Phone, Building2, LayoutGrid, List, GripVertical, MessageSquare, Sparkles, StickyNote, Send, Loader2 } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { PageHeader, AdminCard, Loading, Empty, StatusBadge, Btn, Input, Select, formatDate } from './shared';
import { useToast } from '../../hooks/use-toast';

/**
 * AdminLeads — leads inbox with two complementary views:
 *
 *   • "Kanban" (default): three columns mapped to the existing
 *     new/contacted/closed statuses. Drag a card from one column to
 *     another to update the status server-side (PATCH). Each card
 *     surfaces the lead's name, company, ago-time and a single-line
 *     preview of the message.
 *
 *   • "Table": the previous layout — useful when you want to sort,
 *     filter or bulk-export.
 *
 * Both views share the same data load + the same detail drawer, so
 * "View" + status edits + delete + CSV export keep working in either
 * mode.
 */
const STATUSES = ['all', 'new', 'contacted', 'closed'];
const KANBAN_COLS = [
  { key: 'new',       label: 'New',       tone: 'fuchsia' },
  { key: 'contacted', label: 'In progress', tone: 'amber' },
  { key: 'closed',    label: 'Closed',    tone: 'emerald' },
];
const TONE_HEAD = {
  fuchsia: 'bg-fuchsia-500',
  amber:   'bg-amber-500',
  emerald: 'bg-emerald-500',
};

function timeAgo(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const m = Math.max(0, Math.floor((Date.now() - t) / 60000));
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-GB');
}

export default function AdminLeads() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState('kanban'); // 'kanban' | 'table'
  const [dragOver, setDragOver] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Kanban always wants every status so the columns are populated.
      const params = view === 'kanban' || status === 'all' ? {} : { status };
      const res = await api.get('/api/admin/leads', { params });
      setItems(res.data.items || []);
    } catch (err) {
      toast({ title: 'Failed to load leads', description: formatApiError(err) });
    } finally {
      setLoading(false);
    }
  }, [status, view, toast]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => items.filter((l) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return [l.name, l.email, l.company, l.phone, l.message].some((v) => (v || '').toLowerCase().includes(q));
  }), [items, query]);

  // Group for kanban view.
  const grouped = useMemo(() => {
    const acc = { new: [], contacted: [], closed: [] };
    for (const l of filtered) {
      const k = acc[l.status] ? l.status : 'new';
      acc[k].push(l);
    }
    return acc;
  }, [filtered]);

  const updateStatus = async (id, newStatus) => {
    // Optimistic update so the card moves immediately.
    setItems((arr) => arr.map((l) => (l.id === id ? { ...l, status: newStatus } : l)));
    setSelected((s) => (s && s.id === id ? { ...s, status: newStatus } : s));
    try {
      await api.patch(`/api/admin/leads/${id}`, { status: newStatus });
    } catch (err) {
      toast({ title: 'Update failed', description: formatApiError(err) });
      load();
    }
  };

  const removeLead = async (id) => {
    if (!window.confirm('Delete this lead? This cannot be undone.')) return;
    try {
      await api.delete(`/api/admin/leads/${id}`);
      toast({ title: 'Deleted' });
      setSelected(null);
      load();
    } catch (err) {
      toast({ title: 'Delete failed', description: formatApiError(err) });
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get('/api/admin/leads-export', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'leads.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: 'Export failed', description: formatApiError(err) });
    }
  };

  // ----- AI summarise -----
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);

  // Reply + notes thread.
  const [replyOpen, setReplyOpen] = useState(false);
  const [replySubject, setReplySubject] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [replyBusy, setReplyBusy] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [thread, setThread] = useState([]);

  const loadThread = useCallback(async (lead) => {
    if (!lead) return;
    try {
      const r = await api.get(`/api/admin/leads/${lead.id}/thread`);
      setThread(r.data?.thread || []);
    } catch (_) { setThread([]); }
  }, []);

  // Open a lead in the drawer + load its reply/note thread.
  const openLead = useCallback((lead) => {
    setSelected(lead);
    setAiSummary(null);
    setReplyOpen(false);
    setReplyBody('');
    loadThread(lead);
  }, [loadThread]);

  const sendReply = async () => {
    if (!selected || !replyBody.trim()) return;
    setReplyBusy(true);
    try {
      await api.post(`/api/admin/leads/${selected.id}/reply`, {
        subject: replySubject || 'Re: your VoIPinger enquiry',
        body: replyBody,
      });
      toast({ title: 'Reply sent' });
      setReplyOpen(false);
      setReplyBody('');
      setSelected((s) => ({ ...s, status: 'contacted' }));
      loadThread(selected);
    } catch (err) {
      toast({ title: 'Reply failed', description: formatApiError(err) });
    } finally {
      setReplyBusy(false);
    }
  };

  const addNote = async () => {
    if (!selected || !noteText.trim()) return;
    try {
      await api.post(`/api/admin/leads/${selected.id}/notes`, { text: noteText });
      setNoteText('');
      loadThread(selected);
    } catch (err) {
      toast({ title: 'Note failed', description: formatApiError(err) });
    }
  };
  const summariseLead = async (lead) => {
    setAiBusy(true);
    setAiSummary(null);
    try {
      const res = await api.post(`/api/admin/leads/${lead.id}/ai-summary`, {});
      setAiSummary(res.data?.summary || '(no summary returned)');
    } catch (err) {
      toast({ title: 'AI summary failed', description: formatApiError(err) });
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Contact Leads"
        subtitle="Submissions from the public contact form — drag cards between columns to update status"
        action={
          <div className="flex gap-2 flex-wrap">
            <div className="inline-flex bg-slate-100 p-1 rounded-full">
              {[
                { k: 'kanban', label: 'Kanban', Icon: LayoutGrid },
                { k: 'table',  label: 'Table',  Icon: List },
              ].map(({ k, label, Icon }) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setView(k)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                    view === k ? 'bg-white text-violet-700 shadow' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <Icon size={12} /> {label}
                </button>
              ))}
            </div>
            <Btn variant="ghost" onClick={load} testId="leads-refresh-btn"><RefreshCw size={14} /> Refresh</Btn>
            <Btn onClick={exportCsv} testId="leads-export-btn"><Download size={14} /> Export CSV</Btn>
          </div>
        }
      />

      <AdminCard>
        {/* Shared filter row */}
        <div className="flex flex-col md:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="leads-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, company, message…"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-slate-800/80 border border-white/10 focus:border-fuchsia-400 focus:ring-2 focus:ring-fuchsia-500/30 outline-none text-white text-sm placeholder:text-slate-500"
            />
          </div>
          {view === 'table' && (
            <Select value={status} onChange={(e) => setStatus(e.target.value)} className="md:w-52">
              {STATUSES.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
            </Select>
          )}
        </div>

        {loading ? (
          <Loading />
        ) : view === 'kanban' ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {KANBAN_COLS.map((col) => {
              const list = grouped[col.key] || [];
              const isDragOver = dragOver === col.key;
              return (
                <div
                  key={col.key}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(col.key); }}
                  onDragLeave={() => setDragOver(null)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(null);
                    const id = e.dataTransfer.getData('text/plain');
                    if (id) updateStatus(id, col.key);
                  }}
                  className={`rounded-xl bg-white border ${isDragOver ? 'border-violet-400 ring-2 ring-violet-200' : 'border-slate-200'} flex flex-col min-h-[320px] transition-all`}
                >
                  <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`w-2.5 h-2.5 rounded-full ${TONE_HEAD[col.tone]}`} />
                      <p className="text-xs uppercase tracking-widest font-bold text-slate-700">{col.label}</p>
                    </div>
                    <span className="text-[11px] font-bold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">{list.length}</span>
                  </div>
                  <div className="p-3 space-y-2 flex-1">
                    {list.length === 0 && (
                      <p className="text-xs text-slate-400 text-center py-8">No leads in this column</p>
                    )}
                    {list.map((l) => (
                      <div
                        key={l.id}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData('text/plain', l.id);
                          e.dataTransfer.effectAllowed = 'move';
                        }}
                        onClick={() => openLead(l)}
                        className="group p-3 rounded-lg bg-slate-50 hover:bg-violet-50 border border-slate-200 hover:border-violet-300 cursor-grab active:cursor-grabbing transition-colors"
                      >
                        <div className="flex items-start gap-2">
                          <GripVertical size={12} className="text-slate-300 group-hover:text-violet-500 mt-1 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-slate-900 text-sm truncate">{l.name || '—'}</p>
                            {l.company && <p className="text-[11px] text-slate-500 truncate">{l.company}</p>}
                            {l.message && (
                              <p className="text-xs text-slate-600 mt-1.5 line-clamp-2 leading-snug">{l.message}</p>
                            )}
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-[10px] text-slate-400">{timeAgo(l.created_at)}</span>
                              {l.email && <a onClick={(e) => e.stopPropagation()} href={`mailto:${l.email}`} className="text-[10px] text-violet-600 hover:underline truncate max-w-[120px]">{l.email}</a>}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : filtered.length === 0 ? (
          <Empty>No leads match these filters.</Empty>
        ) : (
          <div className="overflow-x-auto -mx-5 md:-mx-6 px-5 md:px-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-400 uppercase tracking-wider">
                  <th className="py-2 font-semibold">Name</th>
                  <th className="py-2 font-semibold">Email</th>
                  <th className="py-2 font-semibold hidden md:table-cell">Company</th>
                  <th className="py-2 font-semibold hidden lg:table-cell">Date</th>
                  <th className="py-2 font-semibold">Status</th>
                  <th className="py-2 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filtered.map((l) => (
                  <tr key={l.id} className="hover:bg-white/5 transition" data-testid={`lead-row-${l.id}`}>
                    <td className="py-3 font-semibold text-white">{l.name}</td>
                    <td className="py-3 text-slate-300">{l.email}</td>
                    <td className="py-3 text-slate-300 hidden md:table-cell">{l.company || '—'}</td>
                    <td className="py-3 text-slate-400 hidden lg:table-cell">{formatDate(l.created_at)}</td>
                    <td className="py-3"><StatusBadge status={l.status} /></td>
                    <td className="py-3 text-right">
                      <div className="inline-flex gap-1">
                        <Btn size="sm" variant="ghost" onClick={() => openLead(l)} testId={`view-lead-${l.id}`}>
                          <Eye size={12} /> View
                        </Btn>
                        <Btn size="sm" variant="danger" onClick={() => removeLead(l.id)} testId={`delete-lead-${l.id}`}>
                          <Trash2 size={12} />
                        </Btn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminCard>

      {/* Detail drawer (shared by both views) */}
      {selected && (
        <div className="fixed inset-0 z-50 flex" onClick={() => { setSelected(null); setAiSummary(null); }}>
          <div className="flex-1 bg-black/60 backdrop-blur-sm" />
          <aside
            className="w-full max-w-lg bg-slate-900 border-l border-white/10 p-6 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="lead-drawer"
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold text-white">Lead detail</h3>
              <button onClick={() => { setSelected(null); setAiSummary(null); }} className="p-2 rounded-lg hover:bg-white/5" aria-label="close"><X size={18} /></button>
            </div>

            <div className="space-y-4">
              <Row icon={<Mail size={14} />} label="Name"><p className="text-white font-semibold">{selected.name}</p></Row>
              <Row icon={<Mail size={14} />} label="Email"><a href={`mailto:${selected.email}`} className="text-violet-300 hover:text-violet-200">{selected.email}</a></Row>
              {selected.phone && <Row icon={<Phone size={14} />} label="Phone"><a href={`tel:${selected.phone}`} className="text-violet-300">{selected.phone}</a></Row>}
              {selected.company && <Row icon={<Building2 size={14} />} label="Company"><p className="text-white">{selected.company}</p></Row>}
              <Row label="Message">
                <p className="text-slate-200 whitespace-pre-wrap leading-relaxed text-sm">{selected.message || '—'}</p>
              </Row>

              {/* AI summary */}
              <div className="rounded-xl border border-violet-500/30 bg-violet-500/5 p-3">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] font-bold uppercase tracking-widest text-violet-300 flex items-center gap-1.5">
                    <Sparkles size={11} /> AI summary
                  </p>
                  <button
                    type="button"
                    onClick={() => summariseLead(selected)}
                    disabled={aiBusy}
                    className="text-[11px] font-bold px-2.5 py-1 rounded-lg bg-violet-500/20 hover:bg-violet-500/30 text-violet-100 border border-violet-500/30 disabled:opacity-50"
                  >
                    {aiBusy ? 'Summarising…' : aiSummary ? 'Re-run' : 'Summarise'}
                  </button>
                </div>
                {aiSummary && (
                  <p className="text-sm text-slate-200 mt-2 whitespace-pre-wrap leading-relaxed">{aiSummary}</p>
                )}
                {!aiSummary && !aiBusy && (
                  <p className="text-xs text-slate-400 mt-2">Get a 2-3 sentence summary of intent + suggested next action.</p>
                )}
              </div>

              <Row label="Received"><p className="text-slate-300 text-sm">{formatDate(selected.created_at)}</p></Row>
              <Row label="Status">
                <div className="flex flex-wrap gap-2">
                  {['new', 'contacted', 'closed'].map((s) => (
                    <button
                      key={s}
                      onClick={() => updateStatus(selected.id, s)}
                      data-testid={`set-status-${s}`}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider border transition-all ${
                        selected.status === s
                          ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white border-transparent shadow-lg shadow-fuchsia-500/30'
                          : 'bg-white/5 text-slate-300 border-white/10 hover:bg-white/10'
                      }`}
                    >{s}</button>
                  ))}
                </div>
              </Row>

              {/* Thread — replies + notes */}
              {thread.length > 0 && (
                <Row label={`Thread (${thread.length})`}>
                  <div className="space-y-2">
                    {thread.map((t, i) => (
                      <div key={i} className={`rounded-lg border px-3 py-2 ${t.type === 'reply' ? 'bg-violet-500/10 border-violet-500/20' : 'bg-white/5 border-white/10'}`}>
                        <p className="text-[10px] font-bold uppercase tracking-wider mb-1 flex items-center gap-1.5">
                          {t.type === 'reply'
                            ? <span className="text-violet-300 inline-flex items-center gap-1"><MessageSquare size={10} /> Email reply</span>
                            : <span className="text-slate-400 inline-flex items-center gap-1"><StickyNote size={10} /> Internal note</span>}
                          <span className="text-slate-500 font-normal normal-case">· {t.by} · {formatDate(t.at)}</span>
                        </p>
                        {t.subject && <p className="text-xs font-semibold text-slate-200">{t.subject}</p>}
                        <p className="text-xs text-slate-300 whitespace-pre-wrap mt-0.5">{t.body}</p>
                      </div>
                    ))}
                  </div>
                </Row>
              )}

              {/* Quick note */}
              <div className="flex gap-2">
                <input
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addNote(); }}
                  placeholder="Add an internal note…"
                  className="flex-1 px-3 py-2 rounded-lg bg-slate-800/80 border border-white/10 focus:border-fuchsia-400 outline-none text-white text-sm placeholder:text-slate-500"
                />
                <Btn variant="ghost" onClick={addNote} disabled={!noteText.trim()}><StickyNote size={13} /> Note</Btn>
              </div>
            </div>

            {/* Reply composer */}
            {replyOpen ? (
              <div className="mt-5 pt-4 border-t border-white/10 space-y-2">
                <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Reply to {selected.email}</p>
                <input
                  value={replySubject}
                  onChange={(e) => setReplySubject(e.target.value)}
                  placeholder="Re: your VoIPinger enquiry"
                  className="w-full px-3 py-2 rounded-lg bg-slate-800/80 border border-white/10 focus:border-fuchsia-400 outline-none text-white text-sm"
                />
                <textarea
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                  rows={5}
                  placeholder={`Hi ${(selected.name || '').split(' ')[0] || 'there'},\n\nThanks for getting in touch…`}
                  className="w-full px-3 py-2 rounded-lg bg-slate-800/80 border border-white/10 focus:border-fuchsia-400 outline-none text-white text-sm resize-y"
                />
                <div className="flex gap-2">
                  <Btn onClick={sendReply} disabled={replyBusy || !replyBody.trim()}>
                    {replyBusy ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <><Send size={14} /> Send email</>}
                  </Btn>
                  <Btn variant="ghost" onClick={() => setReplyOpen(false)}>Cancel</Btn>
                </div>
                <p className="text-[11px] text-slate-500">Sends via your configured SMTP and logs into the thread above. Sets status → contacted.</p>
              </div>
            ) : (
              <div className="mt-6 flex gap-2 pt-4 border-t border-white/10">
                <Btn onClick={() => { setReplyOpen(true); setReplySubject('Re: your VoIPinger enquiry'); }}>
                  <MessageSquare size={14} /> Reply by email
                </Btn>
                <Btn variant="danger" onClick={() => removeLead(selected.id)}><Trash2 size={14} /> Delete</Btn>
              </div>
            )}
          </aside>
        </div>
      )}
    </>
  );
}

function Row({ label, icon, children }) {
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 flex items-center gap-1.5 mb-1">{icon}{label}</p>
      {children}
    </div>
  );
}
